// src/components/ScoreImport.js
import React, { useState, useEffect } from 'react';
import { flushSync } from 'react-dom';
import * as XLSX from 'xlsx';
import { firestore, functions, auth } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuthState } from 'react-firebase-hooks/auth';
import { collection, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import { getActiveEventId, resolveAdminDefaultEventId } from '../utils/activeEvent';
import './ScoreImport.css';

const importBonusScoresCallable = httpsCallable(functions, 'importBonusScores', {
  timeout: 60000,
});
const importSkyScoresCallable = httpsCallable(functions, 'importSkyScores', {
  timeout: 120000,
});

/** Yield so React can commit `isImporting` and the browser can paint before heavy async work. */
function waitForPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

/** Yield to the macrotask queue so the spinner can paint before large payloads / serialization. */
function yieldToMainThread() {
  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}

/** Cloud Function import only (bonus or sky). Always awaited; does not touch React state. */
async function importScores(params) {
  if (params.importMode === 'bonus') {
    const response = await importBonusScoresCallable(params.bonusPayload);
    return response.data;
  }
  const {
    skySource,
    csvRows,
    csvSkipHeader,
    selectedEvent,
    skyConfig,
    teamCol,
    jumpCol,
    pointsCol,
    bustsCol,
    clearSkyRoundsForTeamsInFile,
  } = params;
  if (skySource === 'csv') {
    const response = await importSkyScoresCallable({
      rows: csvRows,
      skipHeaderRow: csvSkipHeader,
      eventId: selectedEvent || null,
      teamColumn: teamCol,
      jumpColumn: jumpCol,
      pointsColumn: pointsCol,
      bustsColumn: bustsCol,
      clearSkyRoundsForTeamsInFile,
    });
    return response.data;
  }
  const response = await importSkyScoresCallable({
    spreadsheetId: skyConfig.spreadsheetId,
    range: skyConfig.range,
    eventId: selectedEvent || null,
    teamColumn: teamCol,
    jumpColumn: jumpCol,
    pointsColumn: pointsCol,
    bustsColumn: bustsCol,
    clearSkyRoundsForTeamsInFile,
  });
  return response.data;
}

/** Parse CSV text to an array of rows (array of cell strings). Uses `xlsx` for quoted commas. */
function parseCsvTextToRows(text) {
  const clean = text.replace(/^\uFEFF/, '').trim();
  if (!clean) {
    return [];
  }
  const wb = XLSX.read(clean, { type: 'string', raw: false });
  const sheetName = wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', blankrows: false });
  return rows.filter(
    (r) => Array.isArray(r) && r.some((c) => String(c).trim() !== ''),
  );
}

/** User-visible message from Firebase callable / network errors. */
function formatImportError(err) {
  if (!err) {
    return 'Failed to import scores. Please try again.';
  }
  const details = err.details ?? err.customData?.details;
  const detailsMsg =
    typeof details === 'string'
      ? details
      : details && typeof details === 'object' && 'message' in details
        ? String(details.message)
        : '';
  const base =
    err.message ||
    detailsMsg ||
    (typeof err.code === 'string' ? err.code.replace(/_/g, ' ') : '');
  return base || 'Failed to import scores. Please try again.';
}

const ScoreImport = () => {
  const [user, loadingAuth] = useAuthState(auth);
  const [activeEvents, setActiveEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [importMode, setImportMode] = useState('bonus'); // 'bonus' | 'sky'

  const [skyConfig, setSkyConfig] = useState({
    spreadsheetId: '',
    range: 'Sheet1!A2:F1000',
    teamColumn: 0,
    jumpColumn: 1,
    pointsColumn: 4,
    bustsColumn: 5,
  });
  /** Sky data source: uploaded CSV or Google Sheet. */
  const [skySource, setSkySource] = useState('csv');
  const [csvRows, setCsvRows] = useState(null);
  const [csvFileName, setCsvFileName] = useState('');
  const [csvSkipHeader, setCsvSkipHeader] = useState(true);
  /** Delete sky rounds 1–4 for each team named in the file before applying rows (CSV is source of truth). */
  const [clearSkyRoundsForTeamsInFile, setClearSkyRoundsForTeamsInFile] = useState(true);

  // Import configuration
  const [config, setConfig] = useState({
    spreadsheetId: '',
    range: 'Sheet1!A2:D1000',
    teamColumn: 0,
    puzzleColumn: 1,
    scoreColumn: 2,
    eventIdColumn: null,
    matchBy: 'teamNumber'
  });
  
  // Scheduled import settings
  const [scheduledSettings, setScheduledSettings] = useState({
    enabled: false,
    intervalMinutes: 1,
    spreadsheetId: ''
  });
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [saveConfigStatus, setSaveConfigStatus] = useState('');
  const [saveScheduledStatus, setSaveScheduledStatus] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function fetchEvents() {
      try {
        const [querySnapshot, activeId] = await Promise.all([
          getDocs(collection(firestore, 'events')),
          getActiveEventId(),
        ]);
        if (cancelled) return;
        const eventsData = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setActiveEvents(eventsData);
        if (eventsData.length > 0) {
          setSelectedEvent(
            resolveAdminDefaultEventId({ eventIdParam: '', activeEventId: activeId, events: eventsData })
          );
        }
      } catch (err) {
        if (!cancelled) setError('Failed to fetch events: ' + err.message);
      }
    }
    fetchEvents();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch scheduled import settings and saved configuration
  useEffect(() => {
    async function fetchSettings() {
      try {
        const configDoc = await getDoc(doc(firestore, '_config', 'scoreImport'));
        if (configDoc.exists()) {
          const data = configDoc.data();
          setScheduledSettings({
            enabled: data.enabled || false,
            intervalMinutes: data.intervalMinutes || 1,
            spreadsheetId: data.spreadsheetId || ''
          });
          
          // Load saved import configuration
          if (data.spreadsheetId) {
            setConfig(prev => ({
              ...prev,
              spreadsheetId: data.spreadsheetId || prev.spreadsheetId,
              range: data.range || prev.range,
              teamColumn: data.teamColumn ?? prev.teamColumn,
              puzzleColumn: data.puzzleColumn ?? prev.puzzleColumn,
              scoreColumn: data.scoreColumn ?? prev.scoreColumn,
              eventIdColumn: data.eventIdColumn ?? prev.eventIdColumn,
              matchBy: data.matchBy || prev.matchBy
            }));
          }
        }

        const skyDoc = await getDoc(doc(firestore, '_config', 'skyScoreImport'));
        if (skyDoc.exists()) {
          const sd = skyDoc.data();
          setSkyConfig((prev) => ({
            ...prev,
            spreadsheetId: sd.spreadsheetId || prev.spreadsheetId,
            range: sd.range || prev.range,
            teamColumn: sd.teamColumn ?? prev.teamColumn,
            jumpColumn: sd.jumpColumn ?? prev.jumpColumn,
            pointsColumn: sd.pointsColumn ?? prev.pointsColumn,
            bustsColumn: sd.bustsColumn ?? prev.bustsColumn,
          }));
          if (sd.skySource === 'google' || sd.skySource === 'csv') {
            setSkySource(sd.skySource);
          }
          if (typeof sd.skipHeaderRow === 'boolean') {
            setCsvSkipHeader(sd.skipHeaderRow);
          }
          if (typeof sd.clearSkyRoundsForTeamsInFile === 'boolean') {
            setClearSkyRoundsForTeamsInFile(sd.clearSkyRoundsForTeamsInFile);
          }
        }

        setLoadingSettings(false);
      } catch (err) {
        console.error('Error fetching settings:', err);
        setLoadingSettings(false);
      }
    }
    fetchSettings();
  }, []);

  const handleSkyCsvFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    try {
      const text = await file.text();
      const rows = parseCsvTextToRows(text);
      if (rows.length === 0) {
        setError('No rows found in that file.');
        setCsvRows(null);
        setCsvFileName('');
        return;
      }
      setCsvRows(rows);
      setCsvFileName(file.name);
      setError('');
    } catch (err) {
      setError('Could not read CSV: ' + err.message);
      setCsvRows(null);
      setCsvFileName('');
    }
    e.target.value = '';
  };

  const handleImport = async () => {
    if (isImporting) {
      return;
    }
    if (!user) {
      setError('You must be logged in to import scores. Please sign in again.');
      return;
    }

    if (importMode === 'bonus' && !config.spreadsheetId) {
      setError('Spreadsheet ID is required');
      return;
    }
    if (importMode === 'sky' && skySource === 'google' && !skyConfig.spreadsheetId) {
      setError('Spreadsheet ID is required for Google Sheet import');
      return;
    }
    if (importMode === 'sky' && skySource === 'csv' && (!csvRows || csvRows.length === 0)) {
      setError('Choose a CSV file first.');
      return;
    }

    let importParams;
    if (importMode === 'sky') {
      const teamCol = parseInt(skyConfig.teamColumn, 10);
      const jumpCol = parseInt(skyConfig.jumpColumn, 10);
      const pointsCol = parseInt(skyConfig.pointsColumn, 10);
      const bustsCol = parseInt(skyConfig.bustsColumn, 10);
      if ([teamCol, jumpCol, pointsCol, bustsCol].some((n) => isNaN(n))) {
        setError('Invalid sky score column indices.');
        return;
      }
      importParams = {
        importMode: 'sky',
        skySource,
        csvRows,
        csvSkipHeader,
        selectedEvent,
        skyConfig,
        teamCol,
        jumpCol,
        pointsCol,
        bustsCol,
        clearSkyRoundsForTeamsInFile,
      };
    } else {
      const teamCol = parseInt(config.teamColumn, 10);
      const puzzleCol = parseInt(config.puzzleColumn, 10);
      const scoreCol = parseInt(config.scoreColumn, 10);
      const eventIdCol = config.eventIdColumn !== '' && config.eventIdColumn !== null ?
        parseInt(config.eventIdColumn, 10) : null;

      if (isNaN(teamCol) || isNaN(puzzleCol) || isNaN(scoreCol)) {
        setError('Invalid column indices. Please enter valid numbers.');
        return;
      }

      if (eventIdCol !== null && isNaN(eventIdCol)) {
        setError('Invalid event ID column index. Please enter a valid number or leave empty.');
        return;
      }

      importParams = {
        importMode: 'bonus',
        bonusPayload: {
          spreadsheetId: config.spreadsheetId,
          range: config.range,
          matchBy: config.matchBy,
          eventId: selectedEvent || null,
          teamColumn: teamCol,
          puzzleColumn: puzzleCol,
          scoreColumn: scoreCol,
          eventIdColumn: eventIdCol,
        },
      };
    }

    flushSync(() => {
      setError('');
      setResult(null);
      setIsImporting(true);
    });

    try {
      await waitForPaint();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError('Authentication expired. Please refresh the page and sign in again.');
        return;
      }
      await currentUser.getIdToken(true);
      await yieldToMainThread();
      const data = await importScores(importParams);
      setResult(data);
    } catch (err) {
      setError(formatImportError(err));
      console.error('Import error:', err);
    } finally {
      setIsImporting(false);
    }
  };

  const handleSaveImportConfig = async () => {
    try {
      setSaveConfigStatus('');
      if (importMode === 'sky') {
        if (skySource === 'google' && !skyConfig.spreadsheetId) {
          setError('Enter a Spreadsheet ID before saving, or switch to CSV.');
          return;
        }
        await setDoc(
          doc(firestore, '_config', 'skyScoreImport'),
          {
            spreadsheetId: skyConfig.spreadsheetId,
            range: skyConfig.range,
            teamColumn: parseInt(skyConfig.teamColumn, 10) || 0,
            jumpColumn: parseInt(skyConfig.jumpColumn, 10) || 1,
            pointsColumn: parseInt(skyConfig.pointsColumn, 10) || 4,
            bustsColumn: parseInt(skyConfig.bustsColumn, 10) || 5,
            skySource,
            skipHeaderRow: csvSkipHeader,
            clearSkyRoundsForTeamsInFile,
            updatedAt: new Date(),
          },
          { merge: true },
        );
        setError('');
        setSaveConfigStatus('saved');
        setTimeout(() => setSaveConfigStatus(''), 2000);
        return;
      }

      if (!config.spreadsheetId) {
        setError('Please enter a Spreadsheet ID before saving');
        return;
      }

      const configToSave = {
        spreadsheetId: config.spreadsheetId,
        range: config.range,
        teamColumn: parseInt(config.teamColumn) || 0,
        puzzleColumn: parseInt(config.puzzleColumn) || 1,
        scoreColumn: parseInt(config.scoreColumn) || 2,
        eventIdColumn: config.eventIdColumn !== '' && config.eventIdColumn !== null ?
          parseInt(config.eventIdColumn) : null,
        matchBy: config.matchBy,
        updatedAt: new Date(),
      };

      await setDoc(doc(firestore, '_config', 'scoreImport'), configToSave, { merge: true });
      setError('');
      setSaveConfigStatus('saved');
      setTimeout(() => setSaveConfigStatus(''), 2000);
    } catch (err) {
      setError('Failed to save configuration: ' + err.message);
      setSaveConfigStatus('error');
      setTimeout(() => setSaveConfigStatus(''), 2500);
    }
  };

  const handleSaveScheduledSettings = async () => {
    try {
      setSaveScheduledStatus('');
      const settingsToSave = {
        enabled: scheduledSettings.enabled,
        intervalMinutes: parseInt(scheduledSettings.intervalMinutes) || 1,
        updatedAt: new Date()
      };
      
      // Also save import config if it exists
      if (config.spreadsheetId) {
        settingsToSave.spreadsheetId = config.spreadsheetId;
        settingsToSave.range = config.range;
        settingsToSave.teamColumn = parseInt(config.teamColumn) || 0;
        settingsToSave.puzzleColumn = parseInt(config.puzzleColumn) || 1;
        settingsToSave.scoreColumn = parseInt(config.scoreColumn) || 2;
        settingsToSave.eventIdColumn = config.eventIdColumn !== '' && config.eventIdColumn !== null ?
          parseInt(config.eventIdColumn) : null;
        settingsToSave.matchBy = config.matchBy;
      }
      
      await setDoc(doc(firestore, '_config', 'scoreImport'), settingsToSave, { merge: true });
      setError('');
      setSaveScheduledStatus('saved');
      setTimeout(() => setSaveScheduledStatus(''), 2000);
    } catch (err) {
      setError('Failed to save settings: ' + err.message);
      setSaveScheduledStatus('error');
      setTimeout(() => setSaveScheduledStatus(''), 2500);
    }
  };

  if (loadingAuth) {
    return <div className="score-import"><p>Loading...</p></div>;
  }

  if (!user) {
    return (
      <div className="score-import">
        <h2>Import scores (Google Sheets or CSV)</h2>
        <p className="error">You must be logged in to use this feature. Please sign in through the admin panel.</p>
      </div>
    );
  }

  const skyImportReady =
    skySource === 'google'
      ? Boolean(skyConfig.spreadsheetId)
      : Boolean(csvRows && csvRows.length > 0);
  const importReady = importMode === 'bonus' ? Boolean(config.spreadsheetId) : skyImportReady;
  const skySaveReady =
    skySource === 'google' ? Boolean(skyConfig.spreadsheetId) : true;

  return (
    <div className="score-import">
      <h2>Import scores (Google Sheets or CSV)</h2>

      <div className="import-mode-toggle" role="tablist" aria-label="Import type">
        <button
          type="button"
          role="tab"
          aria-selected={importMode === 'bonus'}
          className={`import-mode-tab${importMode === 'bonus' ? ' import-mode-tab--active' : ''}`}
          disabled={isImporting}
          onClick={() => { setImportMode('bonus'); setResult(null); setError(''); }}
        >
          Bonus puzzle scores
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={importMode === 'sky'}
          className={`import-mode-tab${importMode === 'sky' ? ' import-mode-tab--active' : ''}`}
          disabled={isImporting}
          onClick={() => { setImportMode('sky'); setResult(null); setError(''); }}
        >
          Sky scores
        </button>
      </div>

      {importMode === 'bonus' && (
        <p className="info-text">
          Each row maps team + puzzle to a bonus score. Existing Firestore rows for the same team,
          puzzle, and event are updated when you import again.
        </p>
      )}
      {importMode === 'sky' && (
        <p className="info-text">
          Each row is one score for a <strong>competition round</strong> (Jump column must be 1–4).
          Teams are matched by name. If a team has no row for round 2, round 2 is only removed from
          Firestore when &quot;Clear existing rounds…&quot; is checked below; otherwise old manual
          scores for missing rounds stay visible.
        </p>
      )}

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      <div className="import-section">
        <h3>Configuration</h3>

        <div className="form-group">
          <label>Event:</label>
          <select
            value={selectedEvent}
            onChange={(e) => setSelectedEvent(e.target.value)}
            disabled={isImporting}
          >
            {activeEvents.map((event) => (
              <option key={event.id} value={event.id}>{event.title}</option>
            ))}
          </select>
        </div>

        {importMode === 'sky' ? (
          <>
            <div className="form-group sky-source-row">
              <span className="sky-source-label">Sky data source</span>
              <label className="inline-check">
                <input
                  type="radio"
                  name="skySource"
                  checked={skySource === 'csv'}
                  onChange={() => setSkySource('csv')}
                />
                CSV file
              </label>
              <label className="inline-check">
                <input
                  type="radio"
                  name="skySource"
                  checked={skySource === 'google'}
                  onChange={() => setSkySource('google')}
                />
                Google Sheet
              </label>
            </div>

            {skySource === 'csv' ? (
              <div className="form-group">
                <label htmlFor="sky-csv-file">CSV file</label>
                <input
                  id="sky-csv-file"
                  type="file"
                  accept=".csv,text/csv,text/plain"
                  onChange={handleSkyCsvFile}
                />
                <small>
                  Comma-separated; quoted fields supported. UTF-8 (Excel export is fine).
                  {csvFileName ? ` Loaded: ${csvFileName} (${csvRows?.length || 0} rows).` : ''}
                </small>
                <label className="inline-check header-skip">
                  <input
                    type="checkbox"
                    checked={csvSkipHeader}
                    onChange={(e) => setCsvSkipHeader(e.target.checked)}
                  />
                  First row is headers (skip it)
                </label>
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label>Google Sheets ID (required):</label>
                  <input
                    type="text"
                    value={skyConfig.spreadsheetId}
                    onChange={(e) => setSkyConfig({ ...skyConfig, spreadsheetId: e.target.value })}
                    placeholder="From the spreadsheet URL"
                    required
                  />
                  <small>https://docs.google.com/spreadsheets/d/[SPREADSHEET_ID]/edit</small>
                </div>

                <div className="form-group">
                  <label>Sheet Range:</label>
                  <input
                    type="text"
                    value={skyConfig.range}
                    onChange={(e) => setSkyConfig({ ...skyConfig, range: e.target.value })}
                    placeholder="Sheet1!A2:F1000"
                  />
                  <small>Include columns through F if using default mapping (col 6 = busts).</small>
                </div>
              </>
            )}

            <h4>Column mapping (0-based; default = columns 1, 2, 5, 6)</h4>
            <div className="column-mapping">
              <div className="form-group">
                <label>Team name column:</label>
                <input
                  type="number"
                  min="0"
                  value={skyConfig.teamColumn}
                  onChange={(e) => setSkyConfig({ ...skyConfig, teamColumn: e.target.value })}
                />
                <small>Column A = 0 (your column 1)</small>
              </div>
              <div className="form-group">
                <label>Jump / round column:</label>
                <input
                  type="number"
                  min="0"
                  value={skyConfig.jumpColumn}
                  onChange={(e) => setSkyConfig({ ...skyConfig, jumpColumn: e.target.value })}
                />
                <small>Values 1–4, or text containing the round number</small>
              </div>
              <div className="form-group">
                <label>Points (raw) column:</label>
                <input
                  type="number"
                  min="0"
                  value={skyConfig.pointsColumn}
                  onChange={(e) => setSkyConfig({ ...skyConfig, pointsColumn: e.target.value })}
                />
                <small>Your column 5 → index 4</small>
              </div>
              <div className="form-group">
                <label>Busts column:</label>
                <input
                  type="number"
                  min="0"
                  value={skyConfig.bustsColumn}
                  onChange={(e) => setSkyConfig({ ...skyConfig, bustsColumn: e.target.value })}
                />
                <small>Your column 6 → index 5</small>
              </div>
            </div>

            <div className="form-group">
              <label className="inline-check header-skip">
                <input
                  type="checkbox"
                  checked={clearSkyRoundsForTeamsInFile}
                  onChange={(e) => setClearSkyRoundsForTeamsInFile(e.target.checked)}
                />
                Clear sky rounds 1–4 for every team that appears in this file before importing
              </label>
              <small>
                Recommended for full exports: removes rounds that are not in the CSV (e.g. no Jump&nbsp;2 row
                → round 2 cleared). Turn off if you only want to patch individual rounds.
              </small>
            </div>
          </>
        ) : (
          <>
            <div className="form-group">
              <label>Google Sheets ID (required):</label>
              <input
                type="text"
                value={config.spreadsheetId}
                onChange={(e) => setConfig({ ...config, spreadsheetId: e.target.value })}
                placeholder="e.g., 1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                required
              />
              <small>Extract from the Google Sheets URL: https://docs.google.com/spreadsheets/d/[SPREADSHEET_ID]/edit</small>
            </div>

            <div className="form-group">
              <label>Sheet Range:</label>
              <input
                type="text"
                value={config.range}
                onChange={(e) => setConfig({ ...config, range: e.target.value })}
                placeholder="Sheet1!A2:D1000"
              />
              <small>Format: SheetName!StartCell:EndCell (e.g., Sheet1!A2:D1000)</small>
            </div>

            <div className="form-group">
              <label>Match Teams By:</label>
              <select
                value={config.matchBy}
                onChange={(e) => setConfig({ ...config, matchBy: e.target.value })}
              >
                <option value="teamNumber">Team Number</option>
                <option value="teamName">Team Name</option>
              </select>
            </div>

            <h4>Column Mapping (0-based index)</h4>
            <div className="column-mapping">
              <div className="form-group">
                <label>Team Column Index:</label>
                <input
                  type="number"
                  min="0"
                  value={config.teamColumn}
                  onChange={(e) => setConfig({ ...config, teamColumn: e.target.value })}
                />
                <small>Column A = 0, B = 1, C = 2, etc.</small>
              </div>

              <div className="form-group">
                <label>Puzzle Column Index:</label>
                <input
                  type="number"
                  min="0"
                  value={config.puzzleColumn}
                  onChange={(e) => setConfig({ ...config, puzzleColumn: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Score Column Index:</label>
                <input
                  type="number"
                  min="0"
                  value={config.scoreColumn}
                  onChange={(e) => setConfig({ ...config, scoreColumn: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Event ID Column Index (optional):</label>
                <input
                  type="number"
                  min="0"
                  value={config.eventIdColumn || ''}
                  onChange={(e) => setConfig({ ...config, eventIdColumn: e.target.value || null })}
                  placeholder="Leave empty to use selected event"
                />
              </div>
            </div>
          </>
        )}

        <div className="form-group button-group">
          <button
            type="button"
            onClick={handleSaveImportConfig}
            className="save-config-button btn-fm-primary"
            disabled={isImporting || (importMode === 'sky' ? !skySaveReady : !config.spreadsheetId)}
          >
            Save configuration
          </button>
          {saveConfigStatus === 'saved' && (
            <span className="save-feedback success">Saved ✓</span>
          )}
          {saveConfigStatus === 'error' && (
            <span className="save-feedback error">Save failed</span>
          )}
          <button
            type="button"
            onClick={() => void handleImport()}
            disabled={isImporting || !importReady}
            className="import-button btn-fm-primary"
            aria-busy={isImporting}
            aria-label={isImporting ? 'Importing scores, please wait' : 'Import scores'}
          >
            {isImporting ? (
              <>
                <span className="button-spinner" aria-hidden="true" />
                Importing...
              </>
            ) : (
              'Import scores'
            )}
          </button>
        </div>
      </div>

      {importMode === 'bonus' && (
      <div className="import-section">
        <h3>Scheduled Import Settings</h3>
        <p className="info-text">
          Control automatic imports. The system checks every minute but only runs when:
          <br />• Scheduled imports are enabled
          <br />• There's an active event
          <br />• The interval has passed since last import
        </p>
        
        {!loadingSettings && (
          <>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={scheduledSettings.enabled}
                  onChange={(e) => setScheduledSettings({
                    ...scheduledSettings,
                    enabled: e.target.checked
                  })}
                />
                Enable scheduled imports
              </label>
              <small>
                  {scheduledSettings.enabled 
                    ? '✓ Imports will run automatically when there\'s an active event'
                    : '✗ Scheduled imports are disabled (manual imports still work)'}
              </small>
            </div>

            {scheduledSettings.enabled && (
              <div className="form-group">
                <label>Import Interval (minutes):</label>
                <select
                  value={scheduledSettings.intervalMinutes}
                  onChange={(e) => setScheduledSettings({
                    ...scheduledSettings,
                    intervalMinutes: parseInt(e.target.value)
                  })}
                >
                  <option value={1}>Every 1 minute (for testing/active events)</option>
                  <option value={5}>Every 5 minutes</option>
                  <option value={15}>Every 15 minutes</option>
                  <option value={30}>Every 30 minutes</option>
                  <option value={60}>Every hour</option>
                </select>
                <small>
                  Recommended: 1 minute during events, 15+ minutes otherwise
                </small>
              </div>
            )}

            <div className="form-group">
              <div className="button-group">
                <button
                  type="button"
                  onClick={handleSaveScheduledSettings}
                  className="save-settings-button btn-fm-primary"
                >
                  Save Scheduled Settings
                </button>
                {saveScheduledStatus === 'saved' && (
                  <span className="save-feedback success">Saved ✓</span>
                )}
                {saveScheduledStatus === 'error' && (
                  <span className="save-feedback error">Save failed</span>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      )}

      {result && (
        <div className="import-result">
          <h3>Import Results</h3>
          <div className="result-stats">
            <p><strong>Total rows processed:</strong> {result.total}</p>
            <p><strong>New scores imported:</strong> {result.imported}</p>
            <p><strong>Existing scores updated:</strong> {result.updated}</p>
            <p><strong>Skipped rows:</strong> {result.skipped}</p>
            {result.skyRoundsClearedTeams != null && (
              <p><strong>Teams with rounds 1–4 cleared first:</strong> {result.skyRoundsClearedTeams}</p>
            )}
          </div>
          {result.errors && result.errors.length > 0 && (
            <div className="result-errors">
              <h4>Errors ({result.errors.length}):</h4>
              <ul>
                {result.errors.slice(0, 10).map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
                {result.errors.length > 10 && (
                  <li>... and {result.errors.length - 10} more errors</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="help-section">
        <h3>Expected sheet format</h3>
        {importMode === 'sky' ? (
          <>
            <p>
              Sky import from CSV or Google Sheet — same columns (default 0-based indices in parentheses):
            </p>
            <table className="example-table">
              <thead>
                <tr>
                  <th>Col A (0) — Team name</th>
                  <th>Col B (1) — Jump / round</th>
                  <th>…</th>
                  <th>Col E (4) — Points</th>
                  <th>Col F (5) — Busts</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Sky Sharks</td>
                  <td>1</td>
                  <td>…</td>
                  <td>12</td>
                  <td>2</td>
                </tr>
              </tbody>
            </table>
            <p>
              <strong>Note:</strong> Start the range on the first data row (e.g. <code>A2</code> if row 1 is headers).
              Total score stored is <code>points − busts</code>, same as Sky Score Entry.
              Exports like <code>Team, Jump, …, Points, Busts</code> use columns 0, 1, 4, 5 — the defaults.
            </p>
          </>
        ) : (
          <>
            <p>Bonus import — one row per team + puzzle score:</p>
            <table className="example-table">
              <thead>
                <tr>
                  <th>Team {config.matchBy === 'teamNumber' ? 'Number' : 'Name'}</th>
                  <th>Puzzle Number</th>
                  <th>Score Value</th>
                  <th>Event ID (optional)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1</td>
                  <td>5</td>
                  <td>10</td>
                  <td>(optional)</td>
                </tr>
              </tbody>
            </table>
            <p>
              <strong>Note:</strong> Start the range on the first data row. Re-importing updates existing bonus scores
              for the same team, puzzle, and event.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ScoreImport;

